import React from 'react';
import { ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { bracketFormatsFor } from '@/lib/bracket/templates';
import { canImportScores } from '@/lib/bracket/convert';

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right" data-testid={testId}>{value}</span>
  </div>
);

const ScoreImport = ({ draft, eventData, setEventData }) => {
  const ok = canImportScores(draft, eventData);
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="bracket-import-scores">
      <p className="text-sm font-semibold text-amber-900 flex items-center gap-2"><ClipboardCheck className="w-4 h-4" />Scores from your Library bracket</p>
      <ul className="text-xs text-amber-900/80 grid sm:grid-cols-2 gap-x-4">
        {draft.scores.map((s) => <li key={s.match_label}>{s.match_label}: {s.score_a} – {s.score_b}</li>)}
      </ul>
      {ok ? (
        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
          <Checkbox checked={!!eventData.import_confirmed} onCheckedChange={(v) => setEventData((p) => ({ ...p, import_confirmed: !!v }))} data-testid="bracket-import-scores-checkbox" />
          {draft.scores.length === 1 ? 'Import this score as an official result' : `Import these ${draft.scores.length} scores as official results`} (winners advance automatically)
        </label>
      ) : (
        <p className="text-xs text-slate-600" data-testid="bracket-import-scores-blocked">These scores can't be imported because the teams, seeds, matchups or format changed. You can enter them in the tournament after it's created.</p>
      )}
    </div>
  );
};

export const BracketReview = ({ eventData, setEventData, draft }) => {
  const opt = bracketFormatsFor(eventData.teams.length).find((o) => o.format === eventData.format);
  return (
    <Card className="border-slate-200" data-testid="bracket-review">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>BRACKET TOURNAMENT</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Row label="Bracket format" value={opt?.label} testId="bracket-review-format" />
        <Row label="Games" value={opt?.games} />
        <Row label="Byes" value={opt?.byes ? `${opt.byes}${eventData.bracket_positions ? '' : ' (top seeds)'}` : 'None'} testId="bracket-review-byes" />
        <Row label="Matchups" value={eventData.bracket_positions ? 'Arranged by hand' : 'Standard seeding'} testId="bracket-review-matchups" />
        {draft && <Row label="Source" value="Bracket Library" />}
        {draft?.scores?.length > 0 && <ScoreImport draft={draft} eventData={eventData} setEventData={setEventData} />}
      </CardContent>
    </Card>
  );
};
